import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { Reveal } from "./Reveal";
import Logo from "../Logo";
import api from "../../lib/api";

export default function Contact({ contact }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, email y mensaje");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (e) {
      toast.error("No se pudo enviar. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const field = (name, value) => setForm({ ...form, [name]: value });

  return (
    <section id="contacto" className="relative py-28 sm:py-36 bg-jauja-surface" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-jauja-lime">{contact.subheading}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-medium tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1]">
              {contact.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg leading-relaxed text-white/70 max-w-md">{contact.body}</p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {contact.address && (
              <div className="flex items-center gap-4 text-white/80"><MapPin className="text-jauja-lime" size={20} /> {contact.address}</div>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-4 text-white/80 hover:text-jauja-lime transition-colors"><Phone className="text-jauja-lime" size={20} /> {contact.phone}</a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-4 text-white/80 hover:text-jauja-lime transition-colors"><Mail className="text-jauja-lime" size={20} /> {contact.email}</a>
            )}
          </div>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40">Nombre</label>
                <input value={form.name} onChange={(e) => field("name", e.target.value)} data-testid="contact-name" className="mt-2 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30" placeholder="Tu nombre" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40">Empresa</label>
                <input value={form.company} onChange={(e) => field("company", e.target.value)} data-testid="contact-company" className="mt-2 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30" placeholder="Tu empresa" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40">Email</label>
              <input type="email" value={form.email} onChange={(e) => field("email", e.target.value)} data-testid="contact-email" className="mt-2 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30" placeholder="tu@correo.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40">Mensaje</label>
              <textarea rows={4} value={form.message} onChange={(e) => field("message", e.target.value)} data-testid="contact-message" className="mt-2 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30 resize-none" placeholder="Cuéntanos sobre tu desafío" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-full bg-jauja-lime px-8 py-4 text-sm font-bold text-jauja-bg transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100">
              {loading ? "Enviando…" : "Enviar mensaje"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
